import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Segmental retaining wall, 52 ft long, average 3 ft exposed, geogrid every second course', amount: '$7,800' },
  { item: 'Wall drainage: perforated pipe to daylight, clean stone backfill behind the wall', amount: '$1,250' },
  { item: 'Paver walkway, 4 ft wide x 40 ft, on 6 in compacted base with polymeric sand', amount: '$3,600' },
  { item: 'Three natural stone steps set into the slope', amount: '$1,650' },
  { item: 'Optional: matching wall caps glued along the full length', amount: '$880' },
];

const faq = [
  {
    q: 'When does a retaining wall need an engineer?',
    a: 'It depends on wall height, loads above the wall and local rules, and the threshold differs from place to place. Check your local building requirements before quoting tall walls, and say on the estimate who is responsible for any engineering or permit.',
  },
  {
    q: 'How do hardscape contractors price walls and patios?',
    a: 'Walls are often priced by face square foot (length times exposed height), patios and walks by square foot of surface, and steps per step. Excavation, base, drainage and access move the number a lot, which is why they belong on their own lines.',
  },
  {
    q: 'Can I show wall caps or a seat wall as an add-on?',
    a: 'Yes. In Scopie you mark those lines optional, the homeowner ticks them on the emailed estimate, and the total updates before they sign.',
  },
  {
    q: 'Does Scopie do 3D design or material takeoffs?',
    a: 'No. Scopie does not draw designs or calculate block counts. You design and measure; Scopie turns that into an estimate, a signed agreement, a scheduled job and an invoice.',
  },
];

const page: IndustryPage = {
  slug: 'hardscaping',
  order: 21,
  group: 'Outdoor',
  path: '/for/hardscaping',
  primaryKeyword: 'hardscape estimating software',
  secondaryKeywords: ['retaining wall estimate', 'paver installation quote', 'hardscape contractor software'],
  title: 'Hardscape Estimating Software: Walls, Pavers, Steps | Scopie', // 59
  metaDescription:
    'Quote retaining walls, paver walks and stone steps with excavation, base, drainage and geogrid on their own lines, plus caps as an option the client ticks.', // 155
  h1: 'Hardscape estimates for walls, walks and steps',
  answerFirst:
    'Scopie is for hardscape contractors who sell walls, paver walks, patios and steps. Price walls by face area and pavers by surface area, and list excavation, base, drainage and geogrid on their own lines so the work underneath is visible. Caps and seat walls are options the homeowner ticks before signing.',
  exampleQuote: { label: 'Sample wall and walkway estimate', lines, total: '$15,180', depositNote: 'Material deposit and engineering responsibility are the contractor\'s own clauses.' },
  blocks: [
    {
      type: 'callout',
      id: 'underneath',
      text: 'A wall fails from what is behind it and under it. Your estimate should show the drainage, the base and the grid, not only the block.',
    },
    {
      type: 'text',
      id: 'why',
      h2: 'Hardscape is sold on the parts nobody will see',
      lead: '"Why is your wall twice the price of the other bid?"',
      paragraphs: [
        'Two retaining walls can look identical on the day they are finished. One has a compacted base, drainage stone, a pipe to daylight and geogrid tied into the slope; the other has none of it and starts leaning in three years. The only way a homeowner can tell the difference before signing is from the estimate.',
        'Listing excavation, base depth, drainage and reinforcement as their own lines lets you sell the wall you actually build.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A retaining wall, a walk and steps',
      label: 'Sample estimate: backyard slope project',
      lines,
      total: '$15,180',
      totalLabel: 'Total with the wall caps',
      note: 'Illustrative figures for one project. The caps are an optional line.',
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Hardscape estimating mistakes',
      style: 'mistakes',
      items: [
        { title: 'Face area measured from the top only', text: 'Buried courses below grade are real block and labor. Count them.' },
        { title: 'Drainage skipped to win the bid', text: 'It is the first thing to fail and the hardest to add later.' },
        { title: 'Access not priced', text: 'Carrying block by hand through a side yard can triple install time.' },
        { title: 'Spoils left out', text: 'Excavated soil has to go somewhere. Price hauling it.' },
        { title: 'Engineering responsibility unclear', text: 'For taller walls, say who provides any design or permit.' },
      ],
    },
    {
      type: 'list',
      id: 'season',
      h2: 'The hardscape year',
      style: 'bullets',
      items: [
        { title: 'Late winter', text: 'Design meetings and estimates for spring builds.' },
        { title: 'Spring to fall', text: 'Install season, with ground conditions and supply lead times setting the pace.' },
        { title: 'Late fall', text: 'Last installs before frost and a push to sign next year\'s projects.' },
      ],
    },
    {
      type: 'text',
      id: 'build',
      h2: 'From signature to the build',
      paragraphs: [
        'Once the homeowner signs, the job goes on the schedule with the crew and the machines it needs: compact loader, plate compactor, dump trailer. The team can see which machine is out where, and crew members see the address rather than the contract value. Invoices go out by email when each phase is done, and the client stays on file for the next phase.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Hardscape estimate questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/for/concrete', anchor: 'Scopie for concrete contractors', status: 'live' },
    { href: '/resources/how-to-price-landscaping', anchor: 'how to price landscaping jobs', status: 'live' },
  ],
  cta: {
    heading: 'Sell the wall you actually build',
    text: 'Save your base, drainage and block items in Scopie, and email each hardscape estimate with the work underneath on show.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
